import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import _has from 'lodash/has';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { LazyLoadComponent } from 'react-lazy-load-image-component';
import Skeleton from '@material-ui/lab/Skeleton';
import { makeSelectAuth } from 'containers/Auth/selectors';
import {
  makeSelectClientLanding,
  makeSelectClientLandingProcessing,
} from 'containers/ClientLanding/selectors';
import {
  makeSelectClientDetails,
  makeSelectClientDetailsFetching,
} from 'containers/Main/selectors';
import HeroLanding from 'components/HeroLanding';
import PageMeta from 'components/PageMeta';
import VisibleOnScrollTracking from 'components/VisibleOnScrollTracking';
import loadable from 'utils/loadable';
import MixPanel from 'utils/mixpanelService';
import TopicsList from './components/TopicsList';
import Section from './Section';

const Disclaimer = loadable(
  () => import('containers/ResourceAndAssessmentDisclaimer'),
  {
    fallback: null,
  },
);
const ClientFactoids = loadable(() => import('./components/ClientFactoids'), {
  fallback: null,
});

const stateSelector = createStructuredSelector({
  clientDetails: makeSelectClientDetails(),
  clientDetailsFetching: makeSelectClientDetailsFetching(),
  auth: makeSelectAuth(),
  clientLandingInfo: makeSelectClientLanding(),
  clientLandingFetching: makeSelectClientLandingProcessing(),
});

function HomePage() {
  const {
    auth,
    clientDetails,
    clientDetailsFetching,
    clientLandingInfo,
    clientLandingFetching,
  } = useSelector(stateSelector);
  const showAsLanding = !_isEmpty(
    _get(clientDetails, 'landingLinksCollection.items', []),
  );
  useEffect(() => {
    // wait for auth and landing info is loaded so we can identify which variant
    if (
      _has(auth, 'isLoaded') &&
      auth.isLoaded &&
      !_isEmpty(clientLandingInfo) &&
      !clientLandingFetching
    ) {
      MixPanel.track(`Homepage - Loaded and Viewed`);
    }
  }, [auth, clientLandingInfo, clientLandingFetching]);

  if (
    clientDetailsFetching ||
    (showAsLanding && (_isEmpty(clientLandingInfo) || clientLandingFetching))
  )
    return <Skeleton variant="rect" height="500px" />;
  const defaultSections = [
    'Topics',
    'Resources',
    'Blogs',
    'News',
    'Lists',
    'Sponsors',
    'Subscribe',
  ];
  const landingSections = _get(clientLandingInfo, 'optionalSections');
  const finalSections =
    showAsLanding && !_isEmpty(landingSections)
      ? landingSections
      : defaultSections;
  let pageMeta = '';
  let pageType = 'homepage';
  let canonicalUrl = `${window.location.origin}/`;
  if (showAsLanding) {
    pageType = 'landing';
    pageMeta = clientLandingInfo;
    canonicalUrl = `https://${clientDetails.shortName}.crediblemind.com/`;
  }
  return (
    <div id="homepage">
      <PageMeta data={pageMeta} type={pageType} canonicalUrl={canonicalUrl} />
      <HeroLanding />
      <ClientFactoids />
      {finalSections.map(section => {
        if (section === 'Topics') {
          return (
            <VisibleOnScrollTracking section="topics" key={section}>
              <TopicsList
                topicsList={
                  showAsLanding
                    ? {
                        data: _get(
                          clientLandingInfo,
                          'topTopicsCollection.items',
                        ),
                      }
                    : null
                }
              />
            </VisibleOnScrollTracking>
          );
        }
        return <Section section={section} key={section} />;
      })}
      <LazyLoadComponent>
        <Disclaimer type="resources-disclaimer" />
      </LazyLoadComponent>
    </div>
  );
}

export default HomePage;
